export const M10_UNIT_1 = {
  id: "m10-u1-past-continuous",
  title: "Unit 1: Past Continuous",
  topic: "What were you doing? & Collocations",
  iconType: "clock",
  grammarImage: "/m10-u1-past-continuous.jpg",
  grammarText: "Unit focus: Past Continuous (was/were + verb-ing) for actions in progress at a specific past time + verb collocations with make, take, and have.",
  
  sets: [

    // ===================================================================
    // SET 1: WAS/WERE REVIEW (B1)
    // ===================================================================
    {
      id: "m10-u1-set1",
      title: "Set 1: Was & Were",
      context: `*Monday evening - Police station*

Emma arrives at the police station.
The detective asks: "Where were you yesterday?"
Emma thinks about Past Continuous.

*Lunes por la tarde - Estación de policía*

Emma llega a la estación de policía.
El detective pregunta: "¿Dónde estabas ayer?"
Emma piensa en el Pasado Continuo.`,
      
      questions: [
        {
          type: "match",
          q: "Match each subject to the correct auxiliary:",
          pairs: [
            { left: "I / He / She / It", right: "was" },
            { left: "You / We / They", right: "were" },
            { left: "Past Continuous", right: "was/were + verb-ing" }
          ],
          a: "completed",
          successTip: "Past Continuous = was/were + -ing! Used for actions IN PROGRESS at a specific past time! ⏰"
        },
        
        {
          type: "mc",
          q: "Yesterday at 8pm, I ___ at home.",
          options: ["was", "were", "am"],
          a: "was",
          tip: "I, He, She, It = was."
        },
        
        {
          type: "mc",
          q: "At that moment, you ___ in the kitchen.",
          options: ["were", "was", "are"],
          a: "were",
          tip: "You, We, They = were."
        },
        
        {
          type: "mc",
          q: "Last night at midnight, she ___ asleep.",
          options: ["was", "were", "is"],
          a: "was",
          tip: "She = was.",
          successTip: "Sleep se usa como verbo o sustantivo, y asleep describe el estado.\nI sleep 8 hours every day.\nThe baby is asleep."
        },
        
        {
          type: "match",
          q: "Match more subjects:",
          pairs: [
            { left: "He", right: "was" },
            { left: "They", right: "were" },
            { left: "We", right: "were" }
          ],
          a: "completed"
        },
        
        {
          type: "mc",
          q: "Yesterday afternoon, we ___ at the museum.",
          options: ["were", "was", "are"],
          a: "were",
          tip: "We = were."
        },
        
        {
          type: "mc",
          q: "At 7am this morning, they ___ still sleeping.",
          options: ["were", "was", "are"],
          a: "were",
          tip: "They = were."
        },
        
        {
          type: "mc",
          q: "The museum ___ closed at that time.",
          options: ["was", "were", "is"],
          a: "was",
          tip: "Museum (it) = was."
        },
        
        {
          type: "order",
          q: "Traduce: 'Yo estaba en casa ayer.'",
          words: ["I", "was", "at", "home", "yesterday."],
          a: "I was at home yesterday.",
          extra: ["were", "am", "be"]
        },
        
        {
          type: "mc",
          q: "Emma asks: 'Where ___ you at 8pm last night?'",
          options: ["were", "was", "are"],
          a: "were",
          successTip: "'Where were you at [specific time]?' = classic alibi question! Detective English! 🕵️"
        }
      ]
    },

   // ===================================================================
// SET 2: VERB + -ING IN CONTEXT (B1)
// ===================================================================
{
  id: "m10-u1-set2",
  title: "Set 2: Actions in Progress",
  context: `*Tuesday morning - Detective office*

Emma writes her report:
"The suspect was walking near the museum..."
She types carefully, using Past Continuous.

*Martes por la mañana - Oficina del detective*

Emma escribe su reporte:
"El sospechoso estaba caminando cerca del museo..."
Escribe cuidadosamente, usando Pasado Continuo.`,
  
  questions: [
    {
      type: "match",
      q: "Match Emma's report phrases:",
      pairs: [
        { left: "He", right: "was walking" },
        { left: "She was", right: "running" },
        { left: "They", right: "were watching" }
      ],
      a: "completed",
      successTip: "Was/Were + verb-ing! Regular verbs: just add -ing. Walk→walking, watch→watching! 📝"
    },
    
    {
      type: "input",
      q: "Emma types: 'The suspect was ___ near the museum.' (walk)",
      a: "walking",
      alts: ["Walking"],
      tip: "Walk → walking (regular: add -ing)."
    },
    
    {
      type: "mc",
      q: "Emma writes: 'At 8pm, he was ___ away quickly.'",
      options: ["running", "runing", "run"],
      a: "running",
      tip: "Run → running (double 'n').",
      successTip: "Short vowel + single consonant = double it! Run→running, sit→sitting, stop→stopping! ✍️"
    },
    
    {
      type: "input",
      q: "Report: 'The guard was ___ his rounds.' (make)",
      a: "making",
      alts: ["Making"],
      tip: "Make → making (drop 'e', add -ing)."
    },
    
    {
      type: "mc",
      q: "Emma types: 'The witness was ___ to us.'",
      options: ["talking", "takking", "talk"],
      a: "talking",
      tip: "Talk → talking (regular)."
    },
    
    {
      type: "input",
      q: "Report: 'They were ___ in the restaurant.' (sit)",
      a: "sitting",
      alts: ["Sitting"],
      tip: "Sit → sitting (double 't')."
    },
    
    {
      type: "mc",
      q: "Emma writes: 'I was ___ the evidence carefully.'",
      options: ["examining", "examinning", "examine"],
      a: "examining",
      tip: "Examine → examining (regular).",
      successTip: "Examine→examining, listen→listening, watch→watching! 🎯"
    },
    
    {
      type: "input",
      q: "Report: 'The suspect was ___ about his location.' (lie = mentir)",
      a: "lying",
      alts: ["Lying"],
      tip: "Lie → lying (change 'ie' to 'y').",
      successTip: "Lie→lying, die→dying, tie→tying. Special rule: 'ie' becomes 'y' before -ing! 🎯"
    },
    
    {
      type: "order",
      q: "Traduce: 'Ella estaba escribiendo un reporte.'",
      words: ["She", "was", "writing", "a", "report."],
      a: "She was writing a report.",
      extra: ["writting", "write", "an"],
      tip: "Write → writing (drop 'e')."
    },
    
    {
      type: "mc",
      q: "Final report: 'While I was ___, I found new evidence.'",
      options: ["investigating", "investigatting", "investigate"],
      a: "investigating",
      tip: "Investigate → investigating (regular).",
    }
  ]
},

    // ===================================================================
    // SET 3: PAST CONTINUOUS AFFIRMATIVE (B1)
    // ===================================================================
    {
      id: "m10-u1-set3",
      title: "Set 3: Describing Past Actions",
      context: `*Tuesday afternoon - Witness interview*

Emma interviews a witness.
"What were you doing at 8pm?"
"I was having dinner with my family."

*Martes por la tarde - Entrevista a testigo*

Emma entrevista a un testigo.
"¿Qué estabas haciendo a las 8pm?"
"Estaba cenando con mi familia."`,
      
      questions: [
        {
          type: "match",
          q: "Match the sentence parts:",
          pairs: [
            { left: "I", right: "was watching TV" },
            { left: "She was", right: "reading a book" },
            { left: "They", right: "were having dinner" }
          ],
          a: "completed",
        },
        
        {
          type: "mc",
          q: "At 9pm, I ___ a movie.",
          options: ["was watching", "watched", "watch"],
          a: "was watching",
          tip: "Action in progress = Past Continuous."
        },
        
        {
          type: "mc",
          q: "Yesterday at noon, she ___ lunch.",
          options: ["was having", "had", "has"],
          a: "was having",
          tip: "Specific time + in progress."
        },
        
        {
          type: "order",
          q: "Traduce: 'Él estaba trabajando a las 7pm.'",
          words: ["He", "was", "working", "at", "7pm."],
          a: "He was working at 7pm.",
          extra: ["worked", "to", "working"]
        },
        
        {
          type: "mc",
          q: "At that moment, we ___ for the bus.",
          options: ["were waiting", "waited", "wait"],
          a: "were waiting",
          tip: "We were + -ing."
        },
        
        {
          type: "mc",
          q: "Last night at midnight, they ___ still talking.",
          options: ["were", "was", "are"],
          a: "were",
          tip: "They were + verb-ing."
        },
        
        {
          type: "order",
          q: "Traduce: 'Estábamos estudiando en la biblioteca.'",
          words: ["We", "were", "studying", "in", "the", "library."],
          a: "We were studying in the library.",
          extra: ["studied", "study", "are"]
        },
        
        {
          type: "mc",
          q: "The suspect ___ near the museum at 8pm.",
          options: ["was walking", "walked", "walks"],
          a: "was walking",
          tip: "Specific time in past = was/were + -ing."
        },
        
        {
          type: "mc",
          q: "At 6am this morning, I ___ still sleeping.",
          options: ["was", "am", "were"],
          a: "was",
          tip: "I was + verb-ing."
        },
        
        {
          type: "order",
          q: "Traduce: 'Ellos estaban comiendo cuando llamé.'",
          words: ["They", "were", "eating", "when", "I", "called."],
          a: "They were eating when I called.",
          extra: ["ate", "eat", "was"],
          successTip: "'When I called' = interruption! Past Continuous + Past Simple = classic combo! ⚡"
        }
      ]
    },

    // ===================================================================
    // SET 4: PAST CONTINUOUS QUESTIONS (B1)
    // ===================================================================
    {
      id: "m10-u1-set4",
      title: "Set 4: Asking What Happened",
      context: `*Wednesday morning - Investigation continues*

Emma questions another witness.
"What were you doing at 8pm?"
"Where were you standing?"

*Miércoles por la mañana - Continúa la investigación*

Emma interroga a otro testigo.
"¿Qué estabas haciendo a las 8pm?"
"¿Dónde estabas parado?"`,
      
      questions: [
        {
          type: "match",
          q: "Match the question words:",
          pairs: [
            { left: "What", right: "were you doing?" },
            { left: "Where", right: "were you going?" },
            { left: "Why", right: "were you running?" }
          ],
          a: "completed",
          successTip: "Question word + was/were + subject + verb-ing? = Past Continuous question! 🔍"
        },
        
        {
          type: "mc",
          q: "Emma: '___ you doing at 8pm last night?'",
          options: ["What were", "What was", "What are"],
          a: "What were",
          tip: "What were + you + -ing?"
        },
        
        {
          type: "mc",
          q: "'___ she wearing a red dress?'",
          options: ["Was", "Were", "Is"],
          a: "Was",
          tip: "Yes/No question: Was/Were + subject + -ing?"
        },
        
        {
          type: "order",
          q: "Traduce: '¿Dónde estabas trabajando?'",
          words: ["Where", "were", "you", "working?"],
          a: "Where were you working?",
          extra: ["was", "work", "did"]
        },
        
        {
          type: "mc",
          q: "'___ they watching the game at 9pm?'",
          options: ["Were", "Was", "Are"],
          a: "Were",
          tip: "They = were."
        },
        
        {
          type: "mc",
          q: "'Who ___ you talking to?'",
          options: ["were", "was", "are"],
          a: "were",
          tip: "You were + verb-ing."
        },
        
        {
          type: "order",
          q: "Traduce: '¿Qué estaba haciendo él?'",
          words: ["What", "was", "he", "doing?"],
          a: "What was he doing?",
          extra: ["were", "did", "do"]
        },
        
        {
          type: "mc",
          q: "'___ the suspect running away?'",
          options: ["Was", "Were", "Is"],
          a: "Was",
          tip: "Suspect (he/she) = was.",
          successTip: "'The suspect was running' = eyewitness testimony language! Real detective work! 👁️"
        },
        
        {
          type: "mc",
          q: "'How many people ___ standing there?'",
          options: ["were", "was", "are"],
          a: "were",
          tip: "People (plural) = were."
        },
        
        {
          type: "order",
          q: "Traduce: '¿Por qué estabas corriendo?'",
          words: ["Why", "were", "you", "running?"],
          a: "Why were you running?",
          extra: ["was", "run", "did"],
          successTip: "Why were you...? = asking for reasons about past actions! Perfect for investigations! 🕵️"
        }
      ]
    },

    // ===================================================================
    // SET 5: NEGATIVES & CONTRACTIONS (B1+)
    // ===================================================================
    {
      id: "m10-u1-set5",
      title: "Set 5: Saying What Wasn't Happening",
      context: `*Wednesday afternoon - Suspect interrogation*

The suspect denies everything.
"I wasn't there! I wasn't doing anything!"
Emma takes notes carefully.

*Miércoles por la tarde - Interrogatorio del sospechoso*

El sospechoso niega todo.
"¡No estaba ahí! ¡No estaba haciendo nada!"
Emma toma notas cuidadosamente.`,
      
      questions: [
        {
          type: "match",
          q: "Match the contractions:",
          pairs: [
            { left: "was not", right: "wasn't" },
            { left: "were not", right: "weren't" },
            { left: "I was not", right: "I wasn't" }
          ],
          a: "completed",
          successTip: "Wasn't / Weren't = negative Past Continuous! Subject + wasn't/weren't + verb-ing! ❌"
        },
        
        {
          type: "mc",
          q: "I ___ watching TV at that time.",
          options: ["wasn't", "weren't", "am not"],
          a: "wasn't",
          tip: "I = wasn't."
        },
        
        {
          type: "mc",
          q: "They ___ paying attention.",
          options: ["weren't", "wasn't", "aren't"],
          a: "weren't",
          tip: "They = weren't."
        },
        
        {
          type: "order",
          q: "Traduce: 'Él no estaba escuchando.'",
          words: ["He", "wasn't", "listening."],
          a: "He wasn't listening.",
          extra: ["weren't", "didn't", "listen"]
        },
        
        {
          type: "mc",
          q: "She ___ wearing a red dress.",
          options: ["wasn't", "weren't", "isn't"],
          a: "wasn't",
          tip: "She = wasn't."
        },
        
        {
          type: "mc",
          q: "We ___ expecting any visitors.",
          options: ["weren't", "wasn't", "aren't"],
          a: "weren't",
          tip: "We = weren't."
        },
        
        {
          type: "order",
          q: "Traduce: 'No estábamos durmiendo.'",
          words: ["We", "weren't", "sleeping."],
          a: "We weren't sleeping.",
          extra: ["wasn't", "didn't", "sleep"]
        },
        
        {
          type: "mc",
          q: "The witness ___ telling the truth.",
          options: ["wasn't", "weren't", "isn't"],
          a: "wasn't",
          tip: "Witness (singular) = wasn't."
        },
        
        {
          type: "mc",
          q: "You ___ looking at me!",
          options: ["weren't", "wasn't", "aren't"],
          a: "weren't",
          tip: "You = weren't.",
          successTip: "'You weren't looking!' = accusation language! Common in arguments and interrogations! 💢"
        },
        
        {
          type: "order",
          q: "Traduce: 'Ellos no estaban mintiendo.'",
          words: ["They", "weren't", "lying."],
          a: "They weren't lying.",
          extra: ["wasn't", "didn't", "lie"],
          successTip: "Lie→Lying (change 'ie' to 'y'). 'They weren't lying' = defending someone's honesty! 🤥"
        }
      ]
    },

    // ===================================================================
    // SET 6: TIME EXPRESSIONS (B1+)
    // ===================================================================
    {
      id: "m10-u1-set6",
      title: "Set 6: When It Happened",
      context: `*Thursday morning - Timeline creation*

Emma creates a timeline of events.
"At 8pm, the suspect was leaving."
"At that moment, the alarm went off."

*Jueves por la mañana - Creación de cronología*

Emma crea una línea de tiempo.
"A las 8pm, el sospechoso se iba."
"En ese momento, sonó la alarma."`,
      
      questions: [
        {
          type: "match",
          q: "Match time expressions for Past Continuous:",
          pairs: [
            { left: "at 8pm", right: "specific time" },
            { left: "at that moment", right: "that exact time" },
            { left: "while", right: "during" }
          ],
          a: "completed",
          successTip: "Time expressions! 'At [time]', 'at that moment', 'while' = set the scene for Past Continuous! ⏰"
        },
        
        {
          type: "mc",
          q: "___ 9pm, I was having dinner.",
          options: ["At", "In", "On"],
          a: "At",
          tip: "At + specific time."
        },
        
        {
          type: "mc",
          q: "___ that moment, she was running.",
          options: ["At", "In", "On"],
          a: "At",
          tip: "'At that moment' = en ese momento.",
          successTip: "'At' se usa un punto en el tiempo, pero 'in' es para referirse a lo que alguien sentía o experimentaba. 'At that moment, the phone rang'- 'In that moment, I realized.....'"
        },
        
        {
          type: "order",
          q: "Traduce: 'En ese momento, estaba lloviendo.'",
          words: ["At", "that", "moment,", "it", "was", "raining."],
          a: "At that moment, it was raining.",
          extra: ["In", "the", "rained"]
        },
        
        {
          type: "mc",
          q: "___ I was leaving, the phone rang.",
          options: ["While", "During", "At"],
          a: "While",
          tip: "While + Past Continuous = durante.",
          successTip: "While = connects two actions! 'While I was eating, he arrived.' One long, one short! 🔗"
        },
        
        {
          type: "mc",
          q: "I saw her ___ I was walking home.",
          options: ["while", "during", "at"],
          a: "while",
          tip: "While + Past Continuous clause."
        },
        
        {
          type: "order",
          q: "Traduce: 'Mientras esperaba, leí un libro.'",
          words: ["While", "I", "was", "waiting,", "I", "read", "a", "book."],
          a: "While I was waiting, I read a book.",
          extra: ["During", "wait", "reading"]
        },
        
        {
          type: "mc",
          q: "___ the investigation, new evidence appeared.",
          options: ["During", "While", "At"],
          a: "During",
          tip: "During + noun (no verb).",
          successTip: "During vs While! DURING + noun (during dinner). WHILE + clause (while I was eating). 📝"
        },
        
        {
          type: "mc",
          q: "She called ___ I was driving.",
          options: ["while", "during", "at"],
          a: "while",
          tip: "While + Past Continuous."
        },
        
        {
          type: "order",
          q: "Traduce: 'A las 7am, todavía estaba durmiendo.'",
          words: ["At", "7am,", "I", "was", "still", "sleeping."],
          a: "At 7am, I was still sleeping.",
          extra: ["In", "slept", "sleep"],
          successTip: "'Still + Past Continuous' = action continued longer than expected! 'Still sleeping at 10am!' 😴"
        }
      ]
    },

   // ===================================================================
// SET 7: DETECTIVE WORK - Making Sense of Clues (B1+/B2-)
// ===================================================================
{
  id: "m10-u1-set7",
  title: "Set 7: Making Sense of the Case",
  context: `*Friday morning - 9:00 AM - Evidence room*

While Emma **was examining** the evidence, her partner walked in.

"This case doesn't **make** sense!" he said.
"We need to **make** an effort and **take** our time."
"We **have** a chance to solve this!"

Emma **was making** notes when she noticed something strange...

*Viernes por la mañana - 9:00 AM - Sala de evidencias*

Mientras Emma estaba examinando la evidencia, su compañero entró.

"¡Este caso no tiene sentido!" dijo.
"Necesitamos hacer un esfuerzo y tomarnos nuestro tiempo."
"¡Tenemos una oportunidad de resolver esto!"

Emma estaba tomando notas cuando notó algo extraño...`,
  
  questions: [
    {
      type: "match",
      q: "While Emma was working, her partner said three things. Match:",
      pairs: [
        { left: "This doesn't ___ sense", right: "make" },
        { left: "We need to ___ our time", right: "take" },
        { left: "We ___ a chance", right: "have" }
      ],
      a: "completed",
      successTip: "Detective logic! MAKE sense (tiene sentido), TAKE time (toma tiempo), HAVE chance ( tener oportunidad). See the pattern? 🔍"
    },
    
    {
      type: "mc",
      q: "Emma was reviewing clues when she thought: 'This doesn't ___ sense.'",
      options: ["make", "take", "have"],
      a: "make",
      tip: "Make sense = tener sentido."
    },
    
    {
      type: "mc",
      q: "Her partner said: 'Solving this will ___ a lot of time.'",
      options: ["take", "make", "have"],
      a: "take",
      tip: "Take time = requerir/llevar tiempo."
    },
    
    {
      type: "order",
      q: "What was Emma doing? 'Ella estaba tomando notas.'",
      words: ["She", "was", "taking", "notes."],
      a: "She was taking notes.",
      extra: ["reading", "having", "doing"]
    },
    
    {
      type: "mc",
      q: "While they ___ a discussion, new evidence arrived.",
      options: ["were having", "were making", "were taking"],
      a: "were having",
      tip: "Have a discussion = tener una discusión."
    },
    
    {
      type: "mc",
      q: "The detective said: 'We need to ___ an effort to find the truth.'",
      options: ["make", "take", "have"],
      a: "make",
      tip: "Make an effort = hacer un esfuerzo (crear/producir esfuerzo).",
      successTip: "MAKE an effort (hacer un esfuerzo) vs TAKE care (cuidar o encargarse). Different logic! 💡"
    },
    
    {
      type: "order",
      q: "Build: Mientras estaban examinando pistas, hicieron un descubrimiento.",
      words: ["While", "they", "were", "examining", "clues,", "they", "made", "a", "discovery."],
      a: "While they were examining clues, they made a discovery.",
      extra: ["took", "had", "make"]
    },
    
    {
      type: "mc",
      q: "Emma thought: 'We ___ a real chance to solve this case!'",
      options: ["have", "make", "take"],
      a: "have",
      tip: "Have a chance = tener/poseer una oportunidad."
    },
    
    {
      type: "mc",
      q: "Scientists who analyzed the evidence ___ progress yesterday.",
      options: ["made", "took", "had"],
      a: "made",
      tip: "Make progress = hacer progreso."
    },
    
    {
      type: "order",
      q: "What happened? 'Ella estaba haciendo un esfuerzo cuando sonó el teléfono.'",
      words: ["She", "was", "making", "an", "effort", "when", "the", "phone", "rang."],
      a: "She was making an effort when the phone rang.",
      extra: ["taking", "having", "made"]
    },
    
    {
      type: "mc",
      q: "The team leader said: 'This investigation will ___ time, but we'll succeed.'",
      options: ["take", "make", "have"],
      a: "take",
      successTip: "Perfect! Emma's team was MAKING progress while the case was TAKING time! Both happening! ⏰"
    }
  ]
},

// ===================================================================
// SET 8: TAKING CHARGE - Emma's Leadership Moment (B2-)
// ===================================================================
{
  id: "m10-u1-set8",
  title: "Set 8: Taking Charge of the Investigation",
  context: `*Friday afternoon - 3:00 PM - Chief's office*

While Emma **was presenting** her findings, the chief interrupted:

"Emma, you need to **take** charge of this case."
"It will **take** time, but **make** a decision quickly."
"**Have** a meeting with your team and **take** action!"

Emma **was thinking** about her next move when her phone rang...

*Viernes por la tarde - 3:00 PM - Oficina del jefe*

Mientras Emma estaba presentando sus hallazgos, el jefe interrumpió:

"Emma, necesitas hacerte cargo de este caso."
"Llevará tiempo, pero toma una decisión rápido."
"¡Ten una reunión con tu equipo y haz algo!"

Emma estaba pensando en su próximo paso cuando sonó su teléfono...`,
  
  questions: [
    {
      type: "match",
      q: "The chief gave Emma three commands while she was presenting:",
      pairs: [
        { left: "___ charge", right: "Take" },
        { left: "___ a decision", right: "Make" },
        { left: "___ a meeting", right: "Have" }
      ],
      a: "completed",
      successTip: "Leadership verbs! TAKE charge (asumir), MAKE decisions (crear), HAVE meetings (experimentar). Emma's becoming a leader! 👩‍💼"
    },
    
    {
      type: "mc",
      q: "While Emma was listening, the chief said: 'You need to ___ charge.'",
      options: ["take", "make", "have"],
      a: "take",
      tip: "Take charge = asumir el control/hacerse cargo."
    },
    
    {
      type: "mc",
      q: "What was Emma doing when she got the promotion? 'She ___ presenting her findings.'",
      options: ["was", "were", "is"],
      a: "was",
      tip: "Past continuous: was/were + verb-ing. Emma = she = was."
    },
    
    {
      type: "mc",
      q: "The chief warned: 'This investigation will ___ at least two weeks.'",
      options: ["take", "make", "have"],
      a: "take",
      tip: "Take time = llevar/requerir tiempo (it takes time)."
    },
    
    {
      type: "order",
      q: "Emma's thought: 'Necesito tomar una decisión rápida.'",
      words: ["I", "need", "to", "make", "a", "quick", "decision."],
      a: "I need to make a quick decision.",
      extra: ["take", "have", "do"],
      successTip: "MAKE a decision (US common) vs TAKE a decision (UK/formal). Both correct, MAKE more common! 🇺🇸🇬🇧"
    },
    
    {
      type: "mc",
      q: "Emma told her team: 'Let's ___ a meeting tomorrow morning.'",
      options: ["have", "make", "take"],
      a: "have",
      tip: "Have a meeting = tener una reunión."
    },
    
    {
      type: "mc",
      q: "While the team ___ a discussion, Emma was making notes.",
      options: ["was having", "was making", "was taking"],
      a: "was having",
      tip: "Have a discussion. Past continuous: was having."
    },
    
    {
      type: "order",
      q: "Action needed: 'Necesitamos tomar acción inmediatamente.'",
      words: ["We", "need", "to", "take", "action", "immediately."],
      a: "We need to take action immediately.",
      extra: ["make", "have", "do"]
    },
    
    {
      type: "mc",
      q: "Emma decided to ___ responsibility for the entire case.",
      options: ["take", "make", "have"],
      a: "take",
      tip: "Take responsibility = asumir/tomar responsabilidad."
    },
    
    {
      type: "mc",
      q: "The chief said: 'You ___ my full confidence to solve this.'",
      options: ["have", "make", "take"],
      a: "have",
      tip: "Have confidence = tener confianza (poseer).",
      successTip: "HAVE confidence (poseer) vs MAKE confidence (❌ doesn't exist!). Careful! ⚠️"
    },
    
    {
      type: "order",
      q: "Timeline: Mientras Emma estaba pensando, su teléfono sonó.",
      words: ["While", "Emma", "was", "thinking,", "her", "phone", "rang."],
      a: "While Emma was thinking, her phone rang.",
      extra: ["thought", "thinks", "is"]
    }
  ]
},

// ===================================================================
// SET 9: TEAM MEETING - Putting It All Together (B2-)
// ===================================================================
{
  id: "m10-u1-set9",
  title: "Set 9: The Team Meeting - Breakthrough!",
  context: `*Saturday morning - 8:00 AM - Conference room*

While the team **was arriving**, Emma **was preparing** the presentation.

"We're **making** progress!" Emma announced.
"This will **take** time, but we **have** all the evidence now."
"Let's **have** a discussion and **make** a plan."

While they **were discussing** the case, Detective Marco **was taking** notes. Suddenly, he jumped up:

"It all **makes** sense now! I know who did it!"

*Sábado por la mañana - 8:00 AM - Sala de conferencias*

Mientras el equipo estaba llegando, Emma estaba preparando la presentación.

"¡Estamos haciendo progreso!" anunció Emma.
"Llevará tiempo, pero ya tenemos toda la evidencia."
"Tengamos una discusión y hagamos un plan."

Mientras discutían el caso, el Detective Marco estaba tomando notas. De repente, saltó:

"¡Ahora todo tiene sentido! ¡Sé quién lo hizo!"`,
  
  questions: [
    {
      type: "match",
      q: "During the meeting, different things were happening. Match:",
      pairs: [
        { left: "Emma was ___ the meeting", right: "leading" },
        { left: "Marco was ___ notes", right: "taking" },
        { left: "The team was ___ progress", right: "making" }
      ],
      a: "completed",
      successTip: "Multiple actions happening at the same time! Past continuous shows background actions. Detective teamwork! 🕵️‍♀️🕵️‍♂️"
    },
    
    {
      type: "mc",
      q: "What were people doing when the meeting started? 'The team ___ arriving.'",
      options: ["was", "has", "is"],
      a: "was",
      successTip: "The team = singular collective noun = was (UK: were also possible)."
    },
    
    {
      type: "mc",
      q: "Emma announced: 'We're ___ real progress on this case!'",
      options: ["making", "taking", "having"],
      a: "making",
      tip: "Make progress = hacer progreso (present continuous: are making)."
    },
    
    {
      type: "order",
      q: "Emma's plan: 'Tengamos una discusión sobre la evidencia.'",
      words: ["Let's", "have", "a", "discussion", "about", "the", "evidence."],
      a: "Let's have a discussion about the evidence.",
      extra: ["make", "take", "do"]
    },
    
    {
      type: "mc",
      q: "ERROR CHECK: 'While Marco was doing notes...' Fix it!",
      options: ["taking notes", "making notes", "having notes"],
      a: "taking notes",
      tip: "Take notes = tomar apuntes (NOT 'do notes'!).",
      successTip: "Take notes significa escribir apuntes mientras escuchas o lees; make notes significa redactar o preparar notas (normalmente después, organizando ideas)."
    },
    
    {
      type: "mc",
      q: "Emma said: 'This investigation will ___ at least one more week.'",
      options: ["take", "make", "have"],
      a: "take",
      tip: "Take time = llevar/requerir tiempo."
    },
    
    {
      type: "order",
      q: "Breakthrough moment: 'Mientras discutían, Marco tuvo una idea.'",
      words: ["While", "they", "were", "discussing,", "Marco", "had", "an", "idea."],
      a: "While they were discussing, Marco had an idea.",
      extra: ["was", "made", "took"],
    successTip:"Breakthrough moment = momento decisivo o momento de gran avance."
    },
    
    {
      type: "mc",
      q: "Marco shouted: 'It all ___ sense now! I understand everything!'",
      options: ["makes", "takes", "has"],
      a: "makes",
      tip: "Make sense = tener sentido (present: makes)."
    },
    
    {
      type: "mc",
      q: "The team needed to ___ arrangements to arrest the suspect.",
      options: ["make", "take", "have"],
      a: "make",
      tip: "Make arrangements = hacer arreglos/planes."
    },
    
    {
      type: "mc",
      q: "During the meeting, what was happening? 'We ___ a productive discussion.'",
      options: ["were having", "were making", "were taking"],
      a: "were having",
      tip: "Have a discussion. Past continuous: were having.",
      successTip: "HAVE a discussion vs MAKE a decision. Context determines the verb! 🎯"
    },
    
    {
      type: "order",
      q: "Final moment: Emma estaba haciendo un plan cuando llamó el jefe.",
      words: ["Emma", "was", "making", "a", "plan", "when", "the", "chief", "called."],
      a: "Emma was making a plan when the chief called.",
      extra: ["taking", "having", "made"],
      successTip: "Perfect! CASE SOLVED! Past continuous (background) + Past simple (interruption). You've mastered the detective's grammar! 🎉🔍"
    }
  ]
},


    // ===================================================================
    // SET 10: PAST SIMPLE VS PAST CONTINUOUS (B2/B2+)
    // ===================================================================
    {
      id: "m10-u1-set10",
      title: "Set 10: Telling the Complete Story",
      context: `*Sunday evening - Case solved!*

Emma writes her final report:
"While I was investigating, I found the evidence.
The suspect was leaving when we arrived."

*Domingo por la tarde - ¡Caso resuelto!*

Emma escribe su reporte final:
"Mientras investigaba, encontré la evidencia.
El sospechoso se iba cuando llegamos."`,
      
      questions: [
        {
          type: "match",
          q: "Match the use to the tense:",
          pairs: [
            { left: "Completed action", right: "Past Simple" },
            { left: "Action in progress", right: "Past Continuous" },
            { left: "while", right: "mientras" }
          ],
          a: "completed",
          successTip: "Past Simple = completed events. Past Continuous = background/interrupted actions. Master this = narrative power! 📖"
        },
        
        {
          type: "mc",
          q: "While I ___ TV, the phone ___ .",
          options: ["was watching / rang", "watched / was ringing", "was watching / was ringing"],
          a: "was watching / rang",
          tip: "Longer action (continuous) interrupted by shorter (simple).",
          successTip: "While + Past Continuous (long), Past Simple (short interruption). Classic pattern! ⚡"
        },
        
        {
          type: "mc",
          q: "She ___ when I ___ her.",
          options: ["was crying / saw", "cried / was seeing", "was crying / was seeing"],
          a: "was crying / saw",
          tip: "Crying = in progress. Saw = completed."
        },
        
        {
          type: "order",
          q: "Traduce: 'Estaba lloviendo cuando salimos.'",
          words: ["It", "was", "raining", "when", "we", "left."],
          a: "It was raining when we left.",
          extra: ["rained", "leave", "leaving"]
        },
        
        {
          type: "mc",
          q: "I ___ home when I ___ the accident.",
          options: ["was driving / saw", "drove / was seeing", "was driving / was seeing"],
          a: "was driving / saw",
          tip: "Driving = background. Saw = main event."
        },
        
        {
          type: "mc",
          q: "What ___ you ___ when the alarm ___ off?",
          options: ["were / doing / went", "did / do / was going", "were / doing / was going"],
          a: "were / doing / went",
          tip: "Doing = continuous. Went off = simple.",
          successTip: "Alarm went off = Past Simple! 'Go off' = sonar (alarma). Phrasal verb! 🔔"
        },
        
        {
          type: "order",
          q: "Traduce: 'Él estaba corriendo cuando cayó.'",
          words: ["He", "was", "running", "when", "he", "fell."],
          a: "He was running when he fell.",
          extra: ["ran", "fall", "falling"]
        },
        
        {
          type: "mc",
          q: "The suspect ___ when the police ___ .",
          options: ["was escaping / arrived", "escaped / were arriving", "was escaping / were arriving"],
          a: "was escaping / arrived",
          tip: "Escaping = in progress. Arrived = completed."
        },
        
        {
          type: "mc",
          q: "While we ___ dinner, the lights ___ out.",
          options: ["were having / went", "had / were going", "were having / were going"],
          a: "were having / went",
          tip: "Having dinner = continuous. Went out = sudden event.",
          successTip: "Lights went out = se fueron las luces! Another 'go' phrasal verb! Go off, go out, go on! 💡"
        },
        
        {
          type: "order",
          q: "Traduce: 'Estábamos durmiendo cuando escuchamos el ruido.'",
          words: ["We", "were", "sleeping", "when", "we", "heard", "the", "noise."],
          a: "We were sleeping when we heard the noise.",
          extra: ["slept", "hear", "hearing"],
          successTip: "🏆 UNIT 1 COMPLETE! Past Continuous mastered! Now you can report events like a detective! 🕵️ Next: Unit 2!"
        }
      ]
    }
  ],

// ===================================================================
// READING SET (B2) - Multi-Passage Format
// ===================================================================
readingSet: {
  id: "m10-u1-reading",
  title: "Reading: The Museum Robbery Investigation",
  category: "reading",
  
  questions: [
    // PASSAGE 1: INTRODUCTION
    {
      type: "passage",
      passage: `**The Museum Robbery - Detective Emma's Report (Part 1)**

On Monday night at 8pm, a valuable painting was stolen from the City Museum. I was investigating the case all week.

According to witnesses, a man was walking near the museum at 7:45pm. He was wearing a dark jacket and was carrying a large bag. At exactly 8pm, the alarm went off.`
    },
    
    {
      type: "mc",
      q: "When was the painting stolen?",
      options: ["Monday night at 8pm", "Tuesday morning", "7:45pm"],
      a: "Monday night at 8pm",
      tip: "Look for the specific time mentioned."
    },
    
    {
      type: "mc",
      q: "What was the suspicious man carrying?",
      options: ["A large bag", "A painting", "Nothing"],
      a: "A large bag",
      tip: "The witness described what he was carrying."
    },
    
    // PASSAGE 2: SECURITY GUARD'S ACCOUNT
    {
      type: "passage",
      passage: `**Detective Emma's Report (Part 2)**

The security guard, Mr. Johnson, said he was making his rounds when he heard the alarm. He ran to the main hall, but the thief was already escaping through the back door.

"I was checking the west wing when I heard the alarm," Mr. Johnson explained. "By the time I got there, he was gone."`
    },
    
    {
      type: "mc",
      q: "What was the security guard doing when the alarm went off?",
      options: ["Making his rounds", "Sleeping", "Watching TV"],
      a: "Making his rounds",
      tip: "He said 'I was making...'",
      successTip: "'Making his rounds' = doing his patrol. Professional security vocabulary! 👮"
    },
    
    {
      type: "mc",
      q: "How did the thief escape?",
      options: ["Through the back door", "Through the front entrance", "Through a window"],
      a: "Through the back door",
      tip: "The text says 'escaping through the back door'."
    },
    
    // PASSAGE 3: CRIME SCENE EVIDENCE
    {
      type: "passage",
      passage: `**Detective Emma's Report (Part 3)**

While we were examining the crime scene, we found fingerprints on the display case. We also discovered footprints leading to the back exit.

The suspect was leaving evidence everywhere! He clearly wasn't making much of an effort to hide his tracks. This made our job easier.`
    },
    
    {
      type: "mc",
      q: "What were the detectives doing when they found fingerprints?",
      options: ["Examining the crime scene", "Interviewing witnesses", "Making arrests"],
      a: "Examining the crime scene",
      tip: "'While we were examining...'"
    },
    
    {
      type: "mc",
      q: "Was the thief careful?",
      options: ["No, he was leaving evidence everywhere", "Yes, very careful", "The text doesn't say"],
      a: "No, he was leaving evidence everywhere",
      tip: "He wasn't making an effort to hide.",
      successTip: "'Hide his tracks' = cover evidence. Idiom for concealing proof! 🔍"
    },
    
    // PASSAGE 4: WITNESS STATEMENT - PART 1
    {
      type: "passage",
      passage: `**Witness Statement - Mrs. Patricia Williams (Part 1)**

I was having dinner with my husband at the restaurant across from the museum. At around 7:50pm, I was looking out the window when I saw a suspicious man.

He was walking very quickly and was looking around nervously. He was carrying something under his jacket.`
    },
    
    {
      type: "mc",
      q: "What was Mrs. Williams doing at 7:50pm?",
      options: ["Having dinner at a restaurant", "Walking in the street", "Working at the museum"],
      a: "Having dinner at a restaurant",
      tip: "'I was having dinner...'"
    },
    
    {
      type: "mc",
      q: "How was the man behaving?",
      options: ["Walking quickly and looking nervous", "Walking slowly", "Acting normally"],
      a: "Walking quickly and looking nervous",
      tip: "She described him as 'walking very quickly' and 'looking around nervously'."
    },
    
    // PASSAGE 5: WITNESS STATEMENT - PART 2
    {
      type: "passage",
      passage: `**Witness Statement - Mrs. Patricia Williams (Part 2)**

While I was watching him, he went into the museum through a side entrance. I didn't think much of it at the time, but ten minutes later, we heard the alarm.

The police arrived while we were still eating our dessert. I immediately told them what I had seen.`
    },
    
    {
      type: "mc",
      q: "Where did the man enter the museum?",
      options: ["Through a side entrance", "Through the front door", "Through the back door"],
      a: "Through a side entrance",
      tip: "He went through 'a side entrance'."
    },
    
    {
      type: "mc",
      q: "What were Mr. and Mrs. Williams doing when the police arrived?",
      options: ["Eating their dessert", "Paying the bill", "Leaving the restaurant"],
      a: "Eating their dessert",
      tip: "'While we were still eating our dessert'.",
      successTip: "Perfect reading! You understood Past Continuous in context! Real detective reports! 🎯"
    }
  ]
},


  // ===================================================================
  // REVIEW SET (B2+)
  // ===================================================================
  reviewSet: {
    id: "m10-u1-review",
    title: "Unit 1 Review: Past Continuous Master",
    category: "review",
    
    questions: [
      {
        type: "mc",
        q: "Review: At 9pm last night, I ___ TV.",
        options: ["was watching", "watched", "watch"],
        a: "was watching",
        tip: "Specific past time = Past Continuous."
      },
      
      {
        type: "mc",
        q: "Review: What ___ you doing at 8pm?",
        options: ["were", "was", "did"],
        a: "were",
        tip: "Question form: What were you doing?"
      },
      
      {
        type: "mc",
        q: "Review: She ___ listening to me!",
        options: ["wasn't", "weren't", "didn't"],
        a: "wasn't",
        tip: "Negative: wasn't + verb-ing."
      },
      
      {
        type: "mc",
        q: "Review: While I ___ home, I ___ an accident.",
        options: ["was driving / saw", "drove / was seeing", "was driving / was seeing"],
        a: "was driving / saw",
        tip: "Continuous + Simple = background + interruption."
      },
      
      {
        type: "mc",
        q: "Review: This doesn't ___ sense!",
        options: ["make", "do", "have"],
        a: "make",
        tip: "Make sense = tener sentido."
      },
      
      {
        type: "mc",
        q: "Review: The project will ___ a lot of time.",
        options: ["take", "make", "do"],
        a: "take",
        tip: "Take time = llevar tiempo."
      },
      
      {
        type: "mc",
        q: "Review: We need to ___ a discussion.",
        options: ["have", "make", "take"],
        a: "have",
        tip: "Have a discussion = tener una charla."
      },
      
      {
        type: "mc",
        q: "Review: ___ that moment, it started raining.",
        options: ["At", "In", "On"],
        a: "At",
        tip: "At that moment = en ese momento."
      },
      
      {
        type: "mc",
        q: "Review: I saw her ___ I was walking.",
        options: ["while", "during", "when"],
        a: "while",
        tip: "While + Past Continuous clause."
      },
      
      {
        type: "mc",
        q: "Review: They were ___ when I called.",
        options: ["sleeping", "sleep", "slept"],
        a: "sleeping",
        tip: "Past Continuous = was/were + verb-ing.",
        successTip: "🎉 UNIT 1 MASTERED! Past Continuous ✅ Collocations ✅ You're ready for Unit 2! 🚀"
      }
    ]
  }
};
